// components/analytics/components/charts/TopTierChart.tsx
import { Table, Progress, Skeleton } from 'antd';
import { Column } from '@antv/g2plot';
import { useEffect, useRef, useState } from 'react';
import NoDataPlaceholder from '../NoDataPlaceholder';

interface TopTierChartProps {
  data: Array<{
    [key: string]: any;
  }>;
  nameField: string;
  valueField: string;
  metricField?: string;
  metricPrefix?: string;
  height?: number;
  loading?: boolean; // Added
}

export const TopTierChart: React.FC<TopTierChartProps> = ({
  data,
  nameField,
  valueField,
  metricField,
  metricPrefix = '',
  height = 300,
  loading = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Column | null>(null);
  const [isChartReady, setIsChartReady] = useState<boolean>(false);

  const maxValue = data?.length ? Math.max(...data.map(item => Number(item[valueField]))) : 0;

  // Safe chart destruction
  const destroyChart = () => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
        chartRef.current = null;
      } catch (error) {
        console.error('Error destroying TopTierChart:', error);
      }
    }
  };

  // Chart initialization
  const initializeChart = () => {
    const container = containerRef.current;
    if (!container || !data?.length) return;

    try {
      destroyChart();

      chartRef.current = new Column(container, {
        data: data.slice(0, 10),
        xField: nameField,
        yField: valueField,
        label: {
          position: 'top',
          formatter: (v) => `${metricPrefix}${v[valueField]}`
        },
        meta: {
          [valueField]: {
            alias: 'Value',
            formatter: (v) => `${metricPrefix}${v}`
          }
        },
        animation: {
          appear: {
            animation: 'fade-in',
            duration: 1000
          }
        },
        tooltip: {
          shared: true,
          showMarkers: false
        },
        interactions: [
          { type: 'element-active' },
          { type: 'element-highlight' }
        ]
      });

      chartRef.current.render();
      setIsChartReady(true);
    } catch (error) {
      console.error('Error initializing TopTierChart:', error);
      setIsChartReady(false);
    }
  };

  // Handle data updates
  useEffect(() => {
    if (isChartReady && chartRef.current && data?.length) {
      try {
        chartRef.current.changeData(data.slice(0, 10));
      } catch (error) {
        console.error('Error updating TopTierChart data:', error);
        initializeChart();
      }
    } else {
      initializeChart();
    }
  }, [data, nameField, valueField, metricPrefix]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      destroyChart();
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (chartRef.current && containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        chartRef.current.update({
          width,
          height
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: nameField,
      key: nameField,
      width: '30%',
      ellipsis: true,
    },
    {
      title: 'Value',
      dataIndex: valueField,
      key: valueField,
      width: '50%',
      render: (value: number) => (
        <div style={{ minWidth: 150 }}>
          <Progress
            percent={(value / maxValue) * 100}
            format={() => `${metricPrefix}${value.toLocaleString()}`}
            strokeColor="#1890ff"
            trailColor="#f0f0f0"
            size="small"
          />
        </div>
      ),
    },
    ...(metricField ? [{
      title: 'Metric',
      dataIndex: metricField,
      key: metricField,
      width: '20%',
      render: (value: number) => (
        <span>{metricPrefix}{value.toLocaleString()}</span>
      )
    }] : [])
  ];

  // Show skeleton while loading
  if (loading) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (!data?.length) {
    return <NoDataPlaceholder height={height} />;
  }

  return (
    <div>
      <div 
        ref={containerRef} 
        style={{ 
          height, 
          width: '100%',
          marginBottom: 16 
        }} 
      />
      <Table
        dataSource={data}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ y: 240 }}
        rowKey={(record) => record[nameField]}
      />
    </div>
  );
};